import { CENTRAL_CHOICES, PERIPHERAL_ANGLES, timing, variantForLevel, } from './params';
const FEEDBACK_MS = 600;
function pickRandom(arr, n, exclude = []) {
    const pool = arr.filter((x) => !exclude.includes(x));
    const out = [];
    while (out.length < n && pool.length > 0) {
        const i = Math.floor(Math.random() * pool.length);
        out.push(pool.splice(i, 1)[0]);
    }
    return out;
}
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const centralTarget = Math.random() < 0.5 ? CENTRAL_CHOICES[0] : CENTRAL_CHOICES[1];
    const peripheralTargetAngle = PERIPHERAL_ANGLES[Math.floor(Math.random() * PERIPHERAL_ANGLES.length)];
    const distractorAngles = pickRandom(PERIPHERAL_ANGLES, variant.nDistractors, [
        peripheralTargetAngle,
    ]);
    return {
        trialIdx,
        durationMs: Math.max(timing.minRenderMs, durationMs),
        centralTarget,
        peripheralTargetAngle,
        distractorAngles,
    };
}
export function createDoubleDecisionAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        centralAnswer: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    let centralCorrect = false;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* listener erroné ignoré */
            }
        }
    }
    return {
        mount() {
            // Idempotent — pas de DOM imperative ici, le rendu est déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                centralCorrect = false;
                update({
                    subPhase: 'fixation',
                    stim,
                    centralAnswer: null,
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'mask' });
                        timers.push(setTimeout(() => {
                            update({ subPhase: 'awaiting_central' });
                        }, timing.maskMs));
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 simplification : ne fait rien (timers continuent). v2 : freeze.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleCentralPick(choice) {
            if (state.subPhase !== 'awaiting_central')
                return;
            if (!state.stim)
                return;
            centralCorrect = choice === state.stim.centralTarget;
            update({ subPhase: 'awaiting_peripheral', centralAnswer: choice });
        },
        handlePeripheralPick(angle) {
            if (state.subPhase !== 'awaiting_peripheral')
                return;
            const stim = state.stim;
            if (!stim || state.centralAnswer === null)
                return;
            const peripheralCorrect = angle === stim.peripheralTargetAngle;
            const rt = Date.now() - trialStart;
            const resp = {
                centralAnswer: state.centralAnswer,
                peripheralAnswer: angle,
                centralCorrect,
                peripheralCorrect,
                reactionTimeMs: rt,
            };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
/** Helper pour rayon stimulus selon variant.eccentricity. */
export function radiusForVariant(variant) {
    const map = {
        '10deg': 90,
        '20deg': 130,
        '30deg': 160,
    };
    return map[variant.eccentricity];
}
//# sourceMappingURL=adapter.js.map